import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import { donationBankAccountsAPI } from '../../services/api';
import { 
  Heart, 
  Building2,
  Copy,
  Check
} from 'lucide-react';

const DonateContainer = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background.primary};
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  color: ${props => props.theme.colors.white};
  padding: 6rem 2rem 4rem 2rem;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['5xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0 0 1rem 0;
  
  @media (max-width: 768px) {
    font-size: ${props => props.theme.typography.fontSize['3xl']};
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  margin: 0 0 2rem 0;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: ${props => props.theme.typography.fontSize.lg};
  }
`;

const Section = styled.section`
  padding: 5rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  background-color: ${props => props.bgSecondary ? props.theme.colors.background.secondary : 'transparent'};
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  text-align: center;
  margin: 0 0 1rem 0;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark || props.theme.colors.primary});
    margin: 1rem auto 3rem;
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: ${props => props.theme.typography.fontSize['2xl']};
  }
`;

const AccountsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AccountCard = styled(Card)`
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${props => props.theme.shadows.xl};
  }
`;

const AccountContent = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
`;

const AccountTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
  font-family: ${props => props.theme.typography.fontFamily.heading};
`;

const AccountDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0;
  line-height: 1.6;
`;

const BankAccountsSection = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${props => props.theme.colors.border.primary};
`;

const BankAccountCard = styled.div`
  background-color: ${props => props.theme.colors.background.secondary};
  padding: 1rem;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: 0.75rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const BankName = styled.div`
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AccountInfo = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 0.25rem;
  
  strong {
    color: ${props => props.theme.colors.text.primary};
  }
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.sm};
  padding: 0.25rem 0.5rem;
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.text.secondary};
  cursor: pointer;
  margin-left: 0.5rem;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary}10;
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.lg};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.text.secondary};
  
  svg {
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  p {
    font-size: ${props => props.theme.typography.fontSize.base};
    margin: 0;
  }
`;

const DonatePage = () => {
  const { theme } = useTheme();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedIndex, setCopiedIndex] = useState(null);

  useEffect(() => {
    fetchBankAccounts();
  }, []);

  const fetchBankAccounts = async () => {
    try {
      setLoading(true);
      const response = await donationBankAccountsAPI.getAll();
      let accounts = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          accounts = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          accounts = response.data.data;
        }
      }
      setBankAccounts(accounts);
    } catch (error) {
      console.error('Error fetching donation bank accounts:', error);
      setBankAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  };

  return (
    <DonateContainer>
      <PublicHeader theme={theme} />
      
      <HeroSection>
        <HeroContent>
          <Heart size={64} style={{ margin: '0 auto 1rem', opacity: 0.9 }} />
          <HeroTitle>Support Our Mission</HeroTitle>
          <HeroSubtitle>
            Your generous donations help us empower women human rights defenders across Ethiopia
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      {/* Network Official Bank Accounts */}
      <Section>
        <SectionTitle>How to Donate</SectionTitle>
        
        {loading ? (
          <LoadingState>
            <Heart size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
            <p>Loading bank accounts...</p>
          </LoadingState>
        ) : bankAccounts.length === 0 ? (
          <EmptyState>
            <Heart size={48} />
            <p>Official bank accounts will be available here soon.</p>
            <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
              Please check back later for donation information.
            </p>
          </EmptyState>
        ) : (
          <AccountsGrid>
            {bankAccounts.map((account, index) => {
              const baseIndex = `network-${index}`;
              return (
                <AccountCard key={account.id || index}>
                  <AccountContent>
                    <AccountTitle>{account.bank_name}</AccountTitle>
                    {account.description && (
                      <AccountDescription>
                        {account.description}
                      </AccountDescription>
                    )}
                    <BankAccountsSection>
                      <BankName>
                        <Building2 size={16} />
                        Official Bank Account
                      </BankName>
                      <BankAccountCard>
                        <AccountInfo>
                          <strong>Account Name:</strong> {account.account_name}
                          <CopyButton
                            onClick={() => copyToClipboard(account.account_name, `${baseIndex}-name`)}
                            title="Copy account name"
                          >
                            {copiedIndex === `${baseIndex}-name` ? <Check size={12} /> : <Copy size={12} />}
                          </CopyButton>
                        </AccountInfo>
                        <AccountInfo>
                          <strong>Account Number:</strong> {account.account_number}
                          <CopyButton
                            onClick={() => copyToClipboard(account.account_number, `${baseIndex}-number`)}
                            title="Copy account number"
                          >
                            {copiedIndex === `${baseIndex}-number` ? <Check size={12} /> : <Copy size={12} />}
                          </CopyButton>
                        </AccountInfo>
                        {account.branch && (
                          <AccountInfo>
                            <strong>Branch:</strong> {account.branch}
                          </AccountInfo>
                        )}
                        {account.swift_code && (
                          <AccountInfo>
                            <strong>SWIFT:</strong> {account.swift_code}
                          </AccountInfo>
                        )}
                        <AccountInfo>
                          <strong>Currency:</strong> {account.currency || 'ETB'}
                        </AccountInfo>
                      </BankAccountCard>
                    </BankAccountsSection>
                  </AccountContent>
                </AccountCard>
              );
            })}
          </AccountsGrid>
        )}
      </Section>
      
      <PublicFooter theme={theme} />
    </DonateContainer>
  );
};

export default DonatePage;

